import React from "react";
import {
  FileText,
  Building,
  MessageSquare,
  Key,
  Plus,
  Settings,
  Download,
  Calendar,
  TrendingUp,
  Users,
  Activity,
  Target,
  Crosshair,
  Trash2,
  Volume2,
  VolumeX,
  Layers,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { DashboardMetrics, Activity as ActivityType } from "../types";
import { useQuery } from "@tanstack/react-query";
import {
  getDashboardMetrics,
  getRecentActivities,
} from "../services/dashboards";
import Header from "./Header";
import {
  motion,
  AnimatePresence,
  MotionConfig,
  useReducedMotion,
} from "framer-motion";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, translations, token } = useAuth();

  const prefersReducedMotion = useReducedMotion();

  // Ensure trend percentages are consistently formatted with two decimals and sign
  const formatTrend = (value: number | undefined) => {
    if (typeof value !== "number" || Number.isNaN(value)) return undefined;
    const sign = value >= 0 ? "+" : "";
    return `${sign}${value.toFixed(2)}%`;
  };

  const {
    data: metricsRaw,
    isLoading,
  } = useQuery<DashboardMetrics>({
    queryKey: ["dashboardMetrics"],
    queryFn: () => getDashboardMetrics(token!),
    enabled: !!token,
  });
  const {
    data: activitiesRaw,
    isLoading: isLoadingActivities,
  } = useQuery<ActivityType[], Error>({
    queryKey: ["recentActivity"],
    queryFn: () => getRecentActivities(token!),
    enabled: !!token,
  });

  const metrics = metricsRaw ?? ({} as DashboardMetrics);
  const activities = activitiesRaw ?? [];

  const [search, setSearch] = React.useState("");
  const filteredSurveyTargets = React.useMemo(() => {
    const list = metrics.surveyTargets ?? [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((s) =>
      [s.title, s.organizationName ?? "", String(s.targetRequired), String(s.completed)]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [metrics.surveyTargets, search]);

  // Variants
  const pageVariants = {
    hidden: { opacity: 0, y: 12 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: prefersReducedMotion ? 0 : 0.5 },
    },
  };

  const gridVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.06,
        delayChildren: prefersReducedMotion ? 0 : 0.05,
      },
    },
  };

  const listItemVariants = {
    hidden: { opacity: 0, x: -8 },
    show: {
      opacity: 1,
      x: 0,
      transition: { duration: prefersReducedMotion ? 0 : 0.3 },
    },
  };

  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        className="p-6 space-y-6"
        variants={pageVariants}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <Header>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {user?.role === "Administrator"
                ? translations.helloAdmin
                : translations.helloOrganization}{" "}
              <motion.span
                aria-hidden
                className="text-2xl select-none"
                style={{ display: "inline-block", transformOrigin: "75% 75%" }}
                animate={{ rotate: [0, 18, -8, 14, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 4 }}
              >
                👋
              </motion.span>
            </h1>
          </div>
        </Header>

        {/* Metrics Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={gridVariants}
          initial="hidden"
          animate="show"
        >
          {user?.role === "Administrator" ? (
            <>
              <AnimatedMetricCard
                title={translations.totalSurveys}
                value={metrics.totalSurveys ?? 0}
                icon={FileText}
                color="blue"
                trend={formatTrend(metrics.surveyTrendPercentage as unknown as number)}
                isLoading={isLoading}
              />
              <AnimatedMetricCard
                title={translations.activeEntities}
                value={metrics.activeEntities ?? ""}
                icon={Building}
                color="green"
                isLoading={isLoading}
              />
              <AnimatedMetricCard
                title={translations.totalResponses}
                value={metrics.totalResponses ?? 0}
                icon={MessageSquare}
                color="purple"
                trend={formatTrend(metrics.responseTrendPercentage as unknown as number)}
                isLoading={isLoading}
              />
              <AnimatedMetricCard
                title={translations.surveysReachedTarget || "Surveys Reached Target"}
                value={metrics.surveysReachedTarget ?? 0}
                icon={Target}
                color="green"
                isLoading={isLoading}
              />
              <AnimatedMetricCard
                title={translations.surveysReachedTargetPercentage || "% surveys reached target"}
                value={`${(metrics.surveysReachedTargetPercentage ?? 0).toFixed(2)}%`}
                icon={Crosshair}
                color="purple"
                isLoading={isLoading}
              />
              <AnimatedMetricCard
                title={translations.codes || translations.activeCodes}
                value={(metrics.activeCodes ?? 0) + (metrics.inactiveCodes ?? 0)}
                icon={Key}
                color="yellow"
                subtitle={
                  <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {translations.active}: {metrics.activeCodes ?? 0}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      {translations.inactive}: {metrics.inactiveCodes ?? 0}
                    </span>
                  </div>
                }
                isLoading={isLoading}
              />
              {/* Grouped: Questions with/without audio */}
              <AnimatedMetricCard
                title={translations.questionsAudioTitle}
                value={(metrics.questionsWithAudio ?? 0) + (metrics.questionsWithoutAudio ?? 0)}
                icon={Volume2}
                color="purple"
                subtitle={
                  <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                      <Volume2 className="w-3.5 h-3.5 mr-1" /> {translations.questionsWithAudio}: {metrics.questionsWithAudio ?? 0}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      <VolumeX className="w-3.5 h-3.5 mr-1" /> {translations.questionsWithoutAudio}: {metrics.questionsWithoutAudio ?? 0}
                    </span>
                  </div>
                }
                isLoading={isLoading}
              />
              {/* Grouped: Survey types generic/specific */}
              <AnimatedMetricCard
                title={translations.surveyTypesTitle}
                value={(metrics.genericSurveys ?? 0) + (metrics.specificSurveys ?? 0)}
                icon={Layers}
                color="blue"
                subtitle={
                  <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                      {translations.genericSurveys}: {metrics.genericSurveys ?? 0}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                      {translations.specificSurveys}: {metrics.specificSurveys ?? 0}
                    </span>
                  </div>
                }
                isLoading={isLoading}
              />
            </>
          ) : (
            <>
              <AnimatedMetricCard
                title={translations.ownSurveys}
                value={metrics.ownSurveys ?? 0}
                icon={FileText}
                color="blue"
                isLoading={isLoading}
              />
              <AnimatedMetricCard
                title={translations.ownResponses}
                value={metrics.ownResponses ?? 0}
                icon={MessageSquare}
                color="purple"
                isLoading={isLoading}
              />
              <AnimatedMetricCard
                title={translations.codeStatus}
                value={
                  metrics.ownCodeStatus
                    ? translations.active
                    : translations.inactive
                }
                icon={Key}
                color={
                  metrics.ownCodeStatus
                    ? "green"
                    : "red"
                }
                isLoading={isLoading}
                isStatus
              />
              <AnimatedMetricCard
                title={translations.lastPublished}
                value={metrics.lastPublished ?? "-"}
                icon={Calendar}
                color="gray"
                isLoading={isLoading}
                isDate
              />
            </>
          )}
        </motion.div>

        {/* Trends Note */}
        <p className="text-xs text-gray-500">Los incrementos/decrementos se calculan respecto al mes pasado.</p>

        <div className={`grid grid-cols-1 ${
          isLoadingActivities || activities.length > 0 ? "lg:grid-cols-2" : ""
        } gap-6`}>
          {/* Quick Actions */}
          <motion.div
            className="bg-white rounded-lg shadow-sm p-6 border border-gray-200"
            variants={pageVariants}
            initial="hidden"
            animate="show"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2" />
              {translations.quickActions}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {quickActions(user?.role, translations).map((action) => (
                <AnimatedActionButton
                  key={action.id}
                  action={action}
                  onClick={() => {
                    const path = action.id.includes("?") 
                      ? `/${action.id.split("?")[0]}?${action.id.split("?")[1]}`
                      : `/${action.id === "dashboard" ? "dashboard" : action.id}`;
                    navigate(path);
                  }}
                />
              ))}
            </div>
          </motion.div>

          {/* Recent Activity */}
          {(isLoadingActivities || activities.length > 0) && (
            <motion.div
              className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 max-h-64 h-100 overflow-y-auto"
              variants={pageVariants}
              initial="hidden"
              animate="show"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                {translations.recentActivity}
              </h2>
              <div className="space-y-3">
                <AnimatePresence initial={false}>
                  {isLoadingActivities ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <SkeletonActivity key={i} />
                    ))
                  ) : (
                    activities.map((activity, i) => (
                      <motion.div
                        key={activity.id + "-" + i}
                        className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg"
                        variants={listItemVariants}
                        initial="hidden"
                        animate="show"
                        exit={{ opacity: 0, x: 8 }}
                      >
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            {getActivityIcon(activity)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.title}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(activity.timestamp).toLocaleDateString()} •{" "}
                            {activity.user === "system"
                              ? translations.systemUser
                              : activity.user}
                          </p>
                        </div>
                      </motion.div>
                    ))
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </div>

        {/* Survey Targets Section */}
        <motion.div
          className="bg-white rounded-lg shadow-sm p-6 border border-gray-200"
          variants={pageVariants}
          initial="hidden"
          animate="show"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" /> {translations.surveyTargetsTitle}
            </h2>
            <div className="relative w-full sm:w-80">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={translations.searchSurveysLocal}
                aria-label={translations.searchSurveysLocal}
                className="w-full rounded-md border border-gray-300 pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
            </div>
          </div>

          {/* Summary chips */}
          <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
            {isLoading ? (
              <>
                <div className="h-6 w-32 bg-gray-200 rounded-full animate-pulse" />
                <div className="h-6 w-36 bg-gray-200 rounded-full animate-pulse" />
                <div className="h-6 w-40 bg-gray-200 rounded-full animate-pulse" />
              </>
            ) : (
              <>
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                  {translations.totalSurveys}: {metrics.totalSurveys ?? 0}
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-50 text-green-700">
                  {translations.surveysReachedTarget}: {metrics.surveysReachedTarget ?? 0}
                </span>
                {typeof metrics.surveysReachedTargetPercentage === "number" && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-50 text-purple-700">
                    {translations.surveysReachedTargetPercentage}: {(
                      (metrics.surveysReachedTargetPercentage as number) || 0
                    ).toFixed(2)}%
                  </span>
                )}
              </>
            )}
          </div>

          {/* List */}
          <div className="divide-y divide-gray-100">
            {isLoading ? (
              // Skeleton loaders for survey targets
              Array.from({ length: 3 }).map((_, idx) => (
                <div key={`skeleton-${idx}`} className="py-4 flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="flex-shrink-0 w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="h-5 w-20 bg-gray-200 rounded-full animate-pulse" />
                          <div className="h-5 w-24 bg-gray-200 rounded-full animate-pulse" />
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0 space-y-1">
                      <div className="h-4 bg-gray-200 rounded w-32 animate-pulse" />
                      <div className="h-3 bg-gray-200 rounded w-12 ml-auto animate-pulse" />
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div className="h-2 bg-gray-200 rounded-full animate-pulse" style={{ width: `${30 + idx * 25}%` }} />
                  </div>
                </div>
              ))
            ) : filteredSurveyTargets.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center">{translations.noSurveys}</p>
            ) : (
              filteredSurveyTargets.map((s, idx) => {
                const progress = s.targetRequired > 0 ? Math.min(100, Math.round((s.completed / s.targetRequired) * 100)) : 0;
                const isEntity = s.type === 1; // 1 Entidad, 2 Generica
                return (
                  <div key={`${s.title}-${idx}`} className="py-4 flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${isEntity ? "bg-green-50 text-green-600" : "bg-blue-50 text-blue-600"}`}>
                          {isEntity ? <Building className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{s.title}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                              {isEntity ? translations.entity : translations.generic}
                            </span>
                            {s.organizationName && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700">
                                {s.organizationName}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm text-gray-600">
                          {translations.completedLabel}: <span className="font-semibold text-gray-900">{s.completed}</span> {translations.ofLabel} {s.targetRequired}
                        </p>
                        <p className="text-xs text-gray-500">{progress}%</p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div className="h-2 bg-blue-500 rounded-full" style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </motion.div>
      </motion.div>
    </MotionConfig>
  );
}

// Helpers
type Translations = Record<string, string>;
function quickActions(role: string | undefined, t: Translations) {
  const isAdmin = role === "Administrator";
  return isAdmin
    ? [
        {
          id: "surveys?create=true",
          icon: Plus,
          label: t.createSurvey,
          color: "blue",
        },
        {
          id: "questions",
          icon: Settings,
          label: t.editGenericQuestions,
          color: "green",
        },
        { id: "codes", icon: Key, label: t.manageCodes, color: "yellow" },
        {
          id: "export",
          icon: Download,
          label: t.downloadResponses,
          color: "purple",
        },
      ]
    : [
        {
          id: "surveys?create=true",
          icon: Plus,
          label: t.createSurvey,
          color: "blue",
        },
        { id: "codes", icon: Key, label: t.manageCodes, color: "yellow" },
        {
          id: "export",
          icon: Download,
          label: t.downloadResponses,
          color: "purple",
        },
      ];
}

function getColorClasses(color: string) {
  const map: Record<
    string,
    {
      border: string;
      bg: string;
      text: string;
      hoverBg: string;
      hoverBorder: string;
      hoverText: string;
    }
  > = {
    blue: {
      border: "border-blue-300",
      bg: "bg-blue-50",
      text: "text-blue-600",
      hoverBg: "bg-blue-50",
      hoverBorder: "border-blue-400",
      hoverText: "text-blue-700",
    },
    green: {
      border: "border-green-300",
      bg: "bg-green-50",
      text: "text-green-600",
      hoverBg: "bg-green-50",
      hoverBorder: "border-green-400",
      hoverText: "text-green-700",
    },
    purple: {
      border: "border-purple-300",
      bg: "bg-purple-50",
      text: "text-purple-600",
      hoverBg: "bg-purple-50",
      hoverBorder: "border-purple-400",
      hoverText: "text-purple-700",
    },
    yellow: {
      border: "border-yellow-300",
      bg: "bg-yellow-50",
      text: "text-yellow-600",
      hoverBg: "bg-yellow-50",
      hoverBorder: "border-yellow-400",
      hoverText: "text-yellow-700",
    },
    red: {
      border: "border-red-300",
      bg: "bg-red-50",
      text: "text-red-600",
      hoverBg: "bg-red-50",
      hoverBorder: "border-red-400",
      hoverText: "text-red-700",
    },
    gray: {
      border: "border-gray-300",
      bg: "bg-gray-50",
      text: "text-gray-600",
      hoverBg: "bg-gray-50",
      hoverBorder: "border-gray-400",
      hoverText: "text-gray-700",
    },
  };
  return map[color] || map.gray;
}

interface ActionItem {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  color: string;
}

function AnimatedActionButton({
  action,
  onClick,
}: {
  action: ActionItem;
  onClick: () => void;
}) {
  const colors = getColorClasses(action.color);
  const Icon = action.icon as React.ComponentType<{ className?: string }>;
  return (
    <motion.button
      onClick={onClick}
      className={`p-4 rounded-lg border-2 border-dashed ${colors.border} transition-all group bg-white`}
      variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
      whileHover={{ y: -2, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-center space-x-3">
        <Icon
          className={`w-5 h-5 ${
            colors.text
          } group-hover:${colors.hoverText.replace("text-", "text-")}`}
        />
        <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
          {action.label}
        </span>
      </div>
    </motion.button>
  );
}

function SkeletonActivity() {
  return (
    <div className="flex items-start space-x-3 p-3 rounded-lg animate-pulse">
      <div className="w-8 h-8 bg-gray-200 rounded-full" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-2/3" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
        <div className="h-3 bg-gray-200 rounded w-1/3" />
      </div>
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  trend?: string;
  subtitle?: React.ReactNode;
  isStatus?: boolean;
  isDate?: boolean;
  isLoading?: boolean;
}

function AnimatedMetricCard({
  title,
  value,
  icon: Icon,
  color,
  trend,
  subtitle,
  isStatus,
  isDate,
  isLoading,
}: MetricCardProps) {
  const colors = getColorClasses(color);
  return (
    <motion.div
      className="bg-white rounded-lg shadow-sm p-6 border border-gray-200  hover:shadow-md transition-shadow"
      variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
      whileHover="hover"
      initial="hidden"
      animate="show"
    >
        <motion.div
          className="flex items-start justify-between gap-4"
        variants={{ hover: {} }}
      >
          <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <div className="mt-2 flex items-baseline space-x-2">
            {isLoading ? (
              <div className="h-7 w-16 bg-gray-200 rounded animate-pulse" />
            ) : (
              <p
                className={`text-2xl font-bold ${
                  isStatus
                    ? value === "Activo" || value === "Actiu"
                      ? "text-green-600"
                      : "text-red-600"
                    : "text-gray-900"
                }`}
              >
                {isDate && typeof value === "string" && value !== "-"
                  ? new Date(value).toLocaleDateString()
                  : value}
              </p>
            )}
            {trend && !isLoading && (
              <motion.span
                className="text-sm font-medium text-green-600"
                animate={{ y: [0, -2, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                {trend}
              </motion.span>
            )}
          </div>
          {subtitle && (
            <div className="text-xs text-gray-500 mt-1">
              {isLoading ? (
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2">
                  <div className="h-5 w-24 bg-gray-200 rounded-full animate-pulse" />
                  <div className="h-5 w-24 bg-gray-200 rounded-full animate-pulse" />
                </div>
              ) : (
                subtitle
              )}
            </div>
          )}
        </div>
        <motion.div
          className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${colors.bg}`}
          variants={{ hover: { scale: 1.05 } }}
        >
          <Icon className={`w-6 h-6 ${colors.text}`} />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

function getActivityIcon(a: ActivityType) {
  const iconClass = "w-4 h-4";
  // Prefer action-based icons if present (Added / Modified / Deleted)
  const action = (a as unknown as { action?: string })?.action;
  if (action === "Added") return <Plus className={iconClass} />;
  if (action === "Modified") return <Settings className={iconClass} />;
  if (action === "Deleted") return <Trash2 className={iconClass} />;

  // Fallback to type when available
  switch (a.type) {
    case "survey_created":
      return <FileText className={iconClass} />;
    case "survey_published":
      return <Calendar className={iconClass} />;
    case "code_activated":
      return <Key className={iconClass} />;
    case "data_exported":
      return <Download className={iconClass} />;
    default:
      // Entity-based hint as last resort
      if (a.entityName?.toLowerCase().includes("code")) return <Key className={iconClass} />;
      if (a.entityName?.toLowerCase().includes("survey")) return <FileText className={iconClass} />;
      return <Activity className={iconClass} />;
  }
}
